/**
 * Расчёт тепловой карты кликов без внешних библиотек.
 * Точки — доли ширины и высоты картинки (0…1). Плотность считаем на сетке ~1/4 размера экрана,
 * затем окрашиваем по палитре бренда; canvas растягивает её со сглаживанием.
 */

// От холодного к горячему: прозрачный фиолетовый → фиолетовый → коралловый → жёлтый → почти белый
export const HEAT_STOPS = [
    [0, [106, 76, 214, 0]],
    [0.2, [106, 76, 214, 140]],
    [0.45, [164, 143, 240, 190]],
    [0.65, [242, 121, 91, 215]],
    [0.85, [242, 179, 61, 230]],
    [1, [255, 246, 213, 240]],
];

export function heatColor(t) {
    const v = Math.max(0, Math.min(1, t));
    for (let i = 1; i < HEAT_STOPS.length; i += 1) {
        const [p1, c1] = HEAT_STOPS[i];
        const [p0, c0] = HEAT_STOPS[i - 1];
        if (v <= p1) {
            const k = (v - p0) / (p1 - p0 || 1);
            return c0.map((c, j) => Math.round(c + (c1[j] - c) * k));
        }
    }
    return HEAT_STOPS[HEAT_STOPS.length - 1][1];
}

/** Радиус пятна в долях меньшей стороны: мало точек — пятна крупнее, много — точнее. */
export function spotRadius(count) {
    if (count <= 5) return 0.12;
    if (count <= 30) return 0.1;
    if (count <= 150) return 0.08;
    return 0.065;
}

/**
 * Плотность на сетке w×h: сумма гауссовых пятен от каждой точки.
 * Возвращает { grid: Float32Array, max }.
 */
export function densityGrid(points, w, h, radiusShare = spotRadius(points.length)) {
    const grid = new Float32Array(w * h);
    const r = Math.max(2, radiusShare * Math.min(w, h));
    const sigma2 = 2 * (r / 2) * (r / 2);
    const reach = Math.ceil(r * 1.5);
    let max = 0;
    for (const [px, py] of points) {
        const cx = px * (w - 1);
        const cy = py * (h - 1);
        const x0 = Math.max(0, Math.floor(cx - reach));
        const x1 = Math.min(w - 1, Math.ceil(cx + reach));
        const y0 = Math.max(0, Math.floor(cy - reach));
        const y1 = Math.min(h - 1, Math.ceil(cy + reach));
        for (let y = y0; y <= y1; y += 1) {
            const dy = y - cy;
            for (let x = x0; x <= x1; x += 1) {
                const dx = x - cx;
                const value = grid[y * w + x] + Math.exp(-(dx * dx + dy * dy) / sigma2);
                grid[y * w + x] = value;
                if (value > max) max = value;
            }
        }
    }
    return { grid, max };
}

/** RGBA-пиксели тепловой карты для ImageData. Одна точка — уже заметное пятно, а не бледный след. */
export function heatPixels(points, w, h) {
    const { grid, max } = densityGrid(points, w, h);
    const pixels = new Uint8ClampedArray(w * h * 4);
    if (!max) return pixels;
    const norm = Math.max(max, 1.6);
    for (let i = 0; i < grid.length; i += 1) {
        // Корень поднимает средние значения: видно не только пик, но и тёплые места вокруг
        const t = Math.sqrt(grid[i] / norm);
        if (t < 0.12) continue;
        const [r, g, b, a] = heatColor(t);
        pixels.set([r, g, b, a], i * 4);
    }
    return pixels;
}
